import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ExportButton from '../components/ExportButton';
import { getJobStats, getJobs } from '../utils/api';
import { formatCurrency, formatDate, getStatusBadgeClass, getServiceFromJob } from '../utils/format';
import { useAuth } from '../context/AuthContext';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moneyView, setMoneyView] = useState('donut');
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  useEffect(() => {
    Promise.all([getJobStats(), getJobs({ limit: 10 })])
      .then(([s, j]) => { setStats(s); setRecent(j.jobs); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loader"><div className="spinner" /></div>;

  return (
    <>
      <div className="page-header">
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          CPR Live
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--red-100, #fee2e2)', color: '#b91c1c', fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: 1 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', display: 'inline-block', animation: 'cprLivePulse 1.4s infinite' }} />
            Live
          </span>
        </h2>
        <div className="page-header-actions">
          <ExportButton sheet='all' />
          {isAdmin && (
            <button className="btn btn-primary" onClick={() => navigate('/add')}>
              <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/></svg>
              Add Record
            </button>
          )}
        </div>
      </div>

      <div className="page-body">
        <style>{`
          @keyframes cprLivePulse { 0%{opacity:1} 50%{opacity:.3} 100%{opacity:1} }
          .cpr-livecard { border-radius:14px; padding:20px 22px; position:relative; overflow:hidden; color:#fff; }
          .cpr-livecard .cl-label { font-size:13px; text-transform:uppercase; letter-spacing:.6px; opacity:1; margin-bottom:10px; font-weight:700; text-shadow:0 1px 2px rgba(0,0,0,.18); }
          .cpr-livecard .cl-value { font-size:38px; font-weight:900; line-height:1; letter-spacing:-.5px; text-shadow:0 1px 3px rgba(0,0,0,.22); }
          .cpr-livecard .cl-sub { font-size:12.5px; opacity:.95; margin-top:8px; font-weight:500; }
          .cpr-livecard .cl-icon { position:absolute; right:16px; top:16px; opacity:.55; }
        `}</style>
        {/* Stats */}
        <div className="stats-grid">
          {[
            { label: 'Total Jobs', value: stats?.total ?? 0, sub: 'All records', grad: 'linear-gradient(135deg,#2d8a3e,#38b04a)',
              icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
            { label: 'Active Jobs', value: stats?.active ?? 0, sub: 'In progress', grad: 'linear-gradient(135deg,#12c98d,#06a877)',
              icon: 'M13 10V3L4 14h7v7l9-11h-7z' },
            { label: 'Total Contract Value', value: formatCurrency(stats?.total_contract_value), sub: 'All jobs', grad: 'linear-gradient(135deg,#f0a818,#f5c842)', small: true,
              icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
            { label: 'Active Value', value: formatCurrency(stats?.active_contract_value), sub: 'Active jobs only', grad: 'linear-gradient(135deg,#ff8c1a,#f0700a)', small: true,
              icon: 'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z' },
            { label: 'Closed / Funded', value: stats?.closed ?? 0, sub: 'Completed', grad: 'linear-gradient(135deg,#2196f3,#1565c0)',
              icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
            { label: 'Cancelled', value: stats?.cancelled ?? 0, sub: 'Cancelled records', grad: 'linear-gradient(135deg,#ef4444,#c0392b)',
              icon: 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z' },
          ].map((c, i) => (
            <div key={i} className="cpr-livecard" style={{ background: c.grad }}>
              <svg className="cl-icon" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={c.icon} /></svg>
              <div className="cl-label">{c.label}</div>
              <div className="cl-value" style={c.small ? { fontSize: 28 } : undefined}>{c.value}</div>
              <div className="cl-sub">{c.sub}</div>
            </div>
          ))}
        </div>

        {/* Sold / Cost / Profit */}
        {stats?.moneySummary && (() => {
          const m = stats.moneySummary;
          const sold = m.sold || 0, cost = m.cost || 0, profit = m.profit || 0;
          const margin = sold > 0 ? (profit / sold * 100) : 0;
          const hasData = (m.jobs_with_cost || 0) > 0 && sold > 0;
          // Donut geometry
          const C = 2 * Math.PI * 54;
          const costFrac = sold > 0 ? Math.min(Math.max(cost / sold, 0), 1) : 0;
          const profitFrac = Math.min(Math.max(1 - costFrac, 0), 1);
          const costLen = costFrac * C;
          const profitLen = profitFrac * C;
          return (
            <div className="card" style={{ marginBottom: 24 }}>
              <div className="card-header"><h3>Sold · Cost · Profit</h3></div>
              <div className="card-body">
                {!hasData ? (
                  <div style={{ color: 'var(--gray-500)', fontSize: '0.9rem', padding: '8px 0' }}>
                    No jobs have a saved calculator cost yet. Use the Calculator tab on a record and click
                    “Save these numbers to the job” — totals will appear here.
                  </div>
                ) : (
                  <>
                    {/* Always-visible number cards */}
                    <div className="stats-grid" style={{ marginBottom: 20 }}>
                      <div className="stat-card">
                        <div className="stat-label">Total Sold</div>
                        <div className="stat-value" style={{ fontSize: '1.4rem' }}>{formatCurrency(sold)}</div>
                      </div>
                      <div className="stat-card">
                        <div className="stat-label">Total Cost</div>
                        <div className="stat-value" style={{ fontSize: '1.4rem', color: '#854F0B' }}>{formatCurrency(cost)}</div>
                      </div>
                      <div className="stat-card">
                        <div className="stat-label">Profit</div>
                        <div className="stat-value" style={{ fontSize: '1.4rem', color: profit >= 0 ? '#0F6E56' : '#c62828' }}>{formatCurrency(profit)}</div>
                      </div>
                      <div className="stat-card">
                        <div className="stat-label">Margin</div>
                        <div className="stat-value" style={{ fontSize: '1.4rem' }}>{margin.toFixed(1)}%</div>
                      </div>
                    </div>

                    {/* Toggle */}
                    <div style={{ display: 'inline-flex', border: '1px solid var(--gray-200, #ddd)', borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
                      <button type="button" onClick={() => setMoneyView('donut')}
                        style={{ border: 'none', padding: '6px 16px', fontSize: '0.82rem', cursor: 'pointer',
                          background: moneyView === 'donut' ? 'var(--green-600)' : 'transparent',
                          color: moneyView === 'donut' ? '#fff' : 'var(--gray-600, #555)' }}>Donut</button>
                      <button type="button" onClick={() => setMoneyView('bars')}
                        style={{ border: 'none', borderLeft: '1px solid var(--gray-200, #ddd)', padding: '6px 16px', fontSize: '0.82rem', cursor: 'pointer',
                          background: moneyView === 'bars' ? 'var(--green-600)' : 'transparent',
                          color: moneyView === 'bars' ? '#fff' : 'var(--gray-600, #555)' }}>Bars</button>
                    </div>

                    {moneyView === 'donut' ? (
                      <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{ position: 'relative', width: 160, height: 160 }}>
                          <svg viewBox="0 0 140 140" width="160" height="160">
                            <g transform="rotate(-90 70 70)">
                              <circle cx="70" cy="70" r="54" fill="none" stroke="#e9e9e9" strokeWidth="18" />
                              <circle cx="70" cy="70" r="54" fill="none" stroke="#BA7517" strokeWidth="18"
                                strokeDasharray={`${costLen} ${C - costLen}`} />
                              <circle cx="70" cy="70" r="54" fill="none" stroke="#1D9E75" strokeWidth="18"
                                strokeDasharray={`${profitLen} ${C - profitLen}`} strokeDashoffset={`-${costLen}`} />
                            </g>
                          </svg>
                          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <div style={{ fontSize: '1.2rem', fontWeight: 700 }}>{margin.toFixed(1)}%</div>
                            <div style={{ fontSize: '0.7rem', color: 'var(--gray-500)' }}>margin</div>
                          </div>
                        </div>
                        <div style={{ fontSize: '0.86rem', display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 12, height: 12, borderRadius: 3, background: '#BA7517', display: 'inline-block' }} /> Cost &nbsp;{formatCurrency(cost)}</span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 12, height: 12, borderRadius: 3, background: '#1D9E75', display: 'inline-block' }} /> Profit &nbsp;{formatCurrency(profit)}</span>
                          <span style={{ color: 'var(--gray-500)', borderTop: '1px solid var(--gray-200, #eee)', paddingTop: 6 }}>Sold &nbsp;{formatCurrency(sold)}</span>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {[['Sold', sold, '#378ADD'], ['Cost', cost, '#BA7517'], ['Profit', profit, '#1D9E75']].map(([label, val, color]) => {
                          const pct = sold > 0 ? Math.max((val / sold) * 100, 0) : 0;
                          return (
                            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <div style={{ width: 56, fontSize: '0.78rem', color: 'var(--gray-600, #555)' }}>{label}</div>
                              <div style={{ flex: 1, background: 'var(--gray-100, #f0f0f0)', borderRadius: 6, height: 22 }}>
                                <div style={{ width: `${Math.min(pct, 100)}%`, background: color, height: '100%', borderRadius: 6, transition: 'width 0.3s' }} />
                              </div>
                              <div style={{ width: 110, textAlign: 'right', fontSize: '0.82rem', fontWeight: 600 }}>{formatCurrency(val)}</div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div style={{ fontSize: '0.76rem', color: 'var(--gray-500)', marginTop: 14 }}>
                      Based on {m.jobs_with_cost} of {m.total_jobs} jobs that have a saved calculator cost.
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })()}

        {/* Recent jobs */}
        <div className="card">
          <div className="card-header">
            <h3>Recent Jobs</h3>
            <button className="btn btn-outline btn-sm" onClick={() => navigate('/tracker')}>View All</button>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>PO #</th>
                  <th>Customer</th>
                  <th>City</th>
                  <th>Service</th>
                  <th>Sales Rep</th>
                  <th>Contract</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {recent.map(job => (
                  <tr key={job.id} onClick={isAdmin ? () => navigate(`/update?po=${job.po_number}`) : undefined}
                    style={{ cursor: isAdmin ? 'pointer' : 'default' }}>
                    <td className="td-po">{job.po_number}</td>
                    <td className="td-name">{job.customer_name}</td>
                    <td>{job.city || '—'}</td>
                    <td>{getServiceFromJob(job)}</td>
                    <td>{job.sales_rep || '—'}</td>
                    <td className="td-amt">{formatCurrency(job.change_order_amt || job.contract_amt)}</td>
                    <td><span className={getStatusBadgeClass(job.sales_status)}>{job.sales_status || '—'}</span></td>
                    <td>{formatDate(job.contract_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
